import { useEffect } from 'react'
import { motion } from 'framer-motion'
import { Bell, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useNotifications } from '@/hooks/useNotifications'
import NotificationItem from '@/components/list/NotificationItem'

export default function NotificationsPage() {
  const {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    refresh,
  } = useNotifications()

  useEffect(() => {
    refresh()
  }, [refresh])

  const handleMarkAllAsRead = async () => {
    await markAllAsRead()
  }

  const handleMarkAsRead = async (notificationId: string) => {
    await markAsRead(notificationId)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#FF2D7B] to-[#6B3FA0]">
            <Bell className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold gradient-text">
              Notifications
            </h1>
            {unreadCount > 0 && (
              <p className="text-sm text-[#64647A]">
                {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>
        {unreadCount > 0 && (
          <Button
            onClick={handleMarkAllAsRead}
            className="gap-2 bg-[#FF2D7B] hover:bg-[#FF2D7B]/90"
          >
            <Check className="h-4 w-4" />
            Mark all read
          </Button>
        )}
      </div>

      {/* Notifications list */}
      <div className="rounded-xl border border-[#1E1E28] bg-[#12121A] overflow-hidden">
        {isLoading && notifications.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#FF2D7B] border-t-transparent"></div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <Bell className="h-16 w-16 text-[#64647A] mb-4" />
            <h3 className="font-display text-lg font-semibold text-[#FAFAFA] mb-2">
              No notifications yet
            </h3>
            <p className="text-sm text-[#A0A0B0] max-w-sm">
              When someone likes your posts, comments, or sends you a friend request,
              you'll see it here
            </p>
          </div>
        ) : (
          <div className="divide-y divide-[#1E1E28]">
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.notification.id}
                notification={notification}
                onMarkAsRead={handleMarkAsRead}
              />
            ))}
          </div>
        )}
      </div>
    </motion.div>
  )
}
